import type { CrewlinkClient } from '../supabase-client';
import type { CaptureTranscriptVersionRow } from '../database.types';
import { isStaleStatusWrite } from './update-project-status';

/**
 * Writer wrapper for `edit_capture_transcript` (migration 0009). M6 / S06-03.
 *
 * The layering rule (CLAUDE.md §8, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. The screen never calls `.rpc(...)`, and it could
 * not write a transcript version directly even if it tried — no application
 * role holds INSERT on that table.
 *
 * Ownership is not a parameter; it comes from `auth.uid()` inside the function
 * (INV-T23). `expectedVersion` is the concurrency guard: the correction lands
 * only while that version is still current.
 *
 * **THE RETURNED ROW MAY BE THE EXISTING ONE.** When the submitted text
 * matches the stored text after normalization the server writes nothing and
 * returns the current version unchanged — see `isNewTranscriptVersion` below,
 * which is how a caller tells "saved" from "there was nothing to save" without
 * guessing.
 */
export async function editCaptureTranscript(
  client: CrewlinkClient,
  captureId: string,
  body: string,
  expectedVersion: number,
): Promise<CaptureTranscriptVersionRow> {
  const { data, error } = await client.rpc('edit_capture_transcript', {
    p_capture_id: captureId,
    p_body: body,
    p_expected_version: expectedVersion,
  });

  if (error) throw error;
  if (!data) throw new Error('edit_capture_transcript returned no transcript version');

  return data;
}

/**
 * True when the server actually wrote a new version.
 *
 * A no-op save returns the version the caller already had. Comparing the
 * returned number against the expected one is the whole test — and it is asked
 * of the SERVER's answer rather than decided on the client, so the two cannot
 * disagree about whether an edit happened.
 */
export function isNewTranscriptVersion(
  result: CaptureTranscriptVersionRow,
  expectedVersion: number,
): boolean {
  return result.version > expectedVersion;
}

/**
 * Turn a failure into something Brandon can act on.
 *
 * **The stale case gets its own sentence, and it is the one that matters.**
 * "Try again" would be wrong advice: the words on his screen are no longer the
 * words that are stored, and re-sending his correction against them would
 * overwrite an edit he has not read. The honest answer is to say so and show
 * him the current version.
 *
 * `isStaleStatusWrite` is reused rather than re-implemented — the writer
 * raises the same `changed since it was read` / 55000 pair the two status
 * writers do, so one definition of "this was the concurrency guard" serves all
 * three and they cannot drift apart. (I-006 already records that it matches on
 * message text as well as the code; that is unchanged here.)
 */
export function describeTranscriptEditError(message: string, code?: string): string {
  const normalised = message.toLowerCase();

  if (isStaleStatusWrite({ code, message })) {
    return 'This transcript was changed somewhere else. Showing the current version — check it before editing again.';
  }
  if (normalised.includes('no transcript to edit') || normalised.includes('cl004')) {
    return 'This Capture has no transcript to edit.';
  }
  if (normalised.includes('blank') || normalised.includes('22023')) {
    return 'A transcript cannot be empty. Type the corrected words, or cancel.';
  }
  if (normalised.includes('not found') || normalised.includes('p0002')) {
    return 'That Capture is no longer available.';
  }
  if (normalised.includes('not authenticated') || normalised.includes('42501')) {
    return 'Your session has expired. Sign in again and try once more.';
  }
  if (normalised.includes('failed to fetch') || normalised.includes('network')) {
    return 'Could not reach Crewlink. Check your connection and try again.';
  }
  return 'Could not save the correction. Please try again.';
}
